import { useState, type ReactElement, useEffect, useRef } from "react";
import "./style.less";
import Table from "../../shared/layout/table";
import { type TrialDataInterface } from "./types";
import { TrialListColumns } from "./constant";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../shared/utils/translationUtils";
import TrailModal from "../../shared/layout/Modal";
import type { monitorModaldata } from "../../shared/layout/types";
import useDownloadWorkOrder from "../../shared/custom-hooks/useDownloadWorkOrder";
import { Loader } from "../../shared/components/Loader";
import ProtocolDrawer from "../../shared/layout/ProtocolDrawer";
import { TrialAmplitudeEvent } from "../../shared/utils/amplitudeEvents";
import { toast, ToastContainer } from "react-toastify";
import { TrialReportDrawer } from "../../pages/new-trail/TrialReportDrawer";
import PrescriptionExporterMfeLauncher from "../../shared/components/ConnectFiles/PrescriptionExporterModal";
import AgOperationDataLoader from "../new-trail/SummaryComponent/AgOperationDataLoader";
import { useFeatureFlags } from "../../context/LDFlagsContext";
import i18n from "../../i18n";
import {
  fetchTrials,
  fetchTrialById,
  generateReport,
  deleteTrial,
  markTrialComplete,
} from "./trialService";

import TrialsPageHeader from "./pageHeader";
import SearchAndFilter from "./searchAndFilter";
import TableEmptyState from "./tableEmptyState";
import CompletionConfirmationModal from "./completionConfirmationModal";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import { useProperty } from "../../shared/custom-hooks/useProperties";
import { useWorkspace } from "../../shared/custom-hooks/useWorkspace";
import { useAppState } from "../../lib/appContext/AppContext";

const SEARCH_DEBOUNCE_DELAY_MS = 500;

/**
 * creates the main component for displaying and managing trials, including fetching trial data,
 * handling user interactions for creating, deleting, and updating trials, and rendering the trial list with filtering and pagination features.
 * @returns
 */
const TrialsList = (): ReactElement => {
  const { t } = useTranslation();
  const [trialData, setTrialData] = useState<TrialDataInterface[]>([]);
  const { router, orgId, workspaceId, farmId } = useTrialsRouter();
  const [searchText, setSearchText] = useState<string>("");
  const [completionFilter, setCompletionFilter] = useState<string>("all");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isLoading, setLoading] = useState<boolean>(false);
  const { downloadISOXMLOrder, downloadmonitorModalForm } =
    useDownloadWorkOrder();
  const [modalOpen, setModalOpen] = useState(false);
  const [fieldIds, setfieldIds] = useState("");
  const [trailId, setTrialId] = useState("");
  const [protocolId, setProtocolId] = useState("");
  const [open, setOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTrialInfo, setSelectedTrialInfo] = useState<any>(null);
  const [exportAgOperationIds, setExportAgOperationIds] = useState<string[]>(
    []
  );
  // Controls the AgOperationDataLoader modal shown while the latest trial data
  // is refetched before opening the export flow.
  const [agLoaderOpen, setAgLoaderOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reportData, setReportData] = useState<
    { uuid: string | null; reportId: string | null } | undefined
  >(undefined);
  const [, setDrawerLoading] = useState(false);
  const [drawerView, setDrawerView] = useState<"share" | "tree">("share");
  const [treeCheckedKeys, setTreeCheckedKeys] = useState<string[]>([]);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionTrialId, setCompletionTrialId] = useState<string | null>(
    null
  );
  const [completionStatus, setCompletionStatus] = useState<boolean | null>(
    null
  );
  const hasInitializedSearch = useRef(false);
  const extraHeaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const { enableNewWorkorderExport } = useFeatureFlags();
  const trackTrialsEvents = useTrackingEvent({
    page: TrialAmplitudeEvent.TRIALS_PAGE,
    group: TrialAmplitudeEvent.TRIALS_GROUP,
    module: TrialAmplitudeEvent.TRIALS_GROUP,
  });

  const property = useProperty(farmId);
  const workspace = useWorkspace(workspaceId);
  const { stateValue, setStateValue } = useAppState();

  /**
   * to set farm details in app state if not already set, based on the fetched property data.
   * when user changes farm from top bar, need to update it in app state, if not already set
   */
  useEffect(() => {
    if (!stateValue?.farmValue?.name && property.data?.name) {
      setStateValue((prevState: any) => ({
        ...prevState,
        farmValue: {
          name: property.data?.name ?? "",
          id: property.data?.id ?? "",
          details: {
            farmArea: property.data?.total_area ?? 0,
          },
        },
      }));
    }
  }, [stateValue?.farmValue?.id, property]);

  /**
   * when user selects workspace from top bar, need to update it in app state
   */
  useEffect(() => {
    if (
      workspace.data?.name &&
      (!stateValue?.workspaceValue?.id ||
        (stateValue?.orgValue?.workspace_id &&
          stateValue?.workspaceValue?.id &&
          stateValue?.workspaceValue?.id !==
            stateValue?.orgValue?.workspace_id))
    ) {
      setStateValue((prevState: any) => ({
        ...prevState,
        workspaceValue: {
          name: workspace.data?.name ?? "",
          id: workspace.data?.id ?? "",
        },
      }));
    }
  }, [stateValue?.orgValue?.workspace_id, workspace]);

  /**
   * Generates a report for the specified trial IDs and field IDs.
   * @param trialId An array of trial IDs for which the report should be generated.
   * @param fieldIds An array of field IDs to include in the report.
   * @returns A promise that resolves to an object containing the UUID and report ID of the generated report.
   */
  const handleGenerateReport = async (
    trialId: string[],
    fieldIds: string[]
  ): Promise<{ uuid: string; reportId: string }> => {
    try {
      const response = await generateReport(trialId, fieldIds);
      const { uuid, reportId } = response;
      if (!uuid || !reportId) {
        throw new Error("No report UUID or reportId received in response");
      }
      setReportData(response);
      return response;
    } catch (error) {
      setReportData({ uuid: null, reportId: null });
      throw error;
    } finally {
      setDrawerLoading(false);
    }
  };

  /**
   * handles the action of sharing a report for a specific trial. It sets the selected trial information,
   * generates a report if it hasn't been generated already for the selected trial, and then opens the drawer to display the report sharing options.
   * Only generate if not already generated for this trial
   * @param trialInfo The information about the trial for which the report is to be shared.
   */
  const handleShareReport = async (trialInfo: any): Promise<void> => {
    setSelectedTrialInfo(trialInfo);
    if (!reportData?.uuid || selectedTrialInfo?.id !== trialInfo.id) {
      await handleGenerateReport([trialInfo.id], trialInfo.field_ids);
    }

    setDrawerVisible(true);
  };

  /**
   * hides the report sharing drawer by resetting the relevant state variables,
   * including hiding the drawer, clearing the selected trial information, resetting the report data, and setting the drawer view back to its default state.
   */
  const hideDrawer = (): void => {
    setDrawerVisible(false);
    setSelectedTrialInfo(null);
    setReportData(undefined);
    setDrawerView("share");
  };

  /**
   * Handles the OK action for the monitor modal.
   * @param data The data from the monitor modal.
   * @param fielFormdData The form data from the monitor modal.
   */
  const handleOk = (data: monitorModaldata, fielFormdData: any): void => {
    if (
      data.selectedmonitorBrand === "isoxml" ||
      data.selectedmonitorModel === "isoxml"
    ) {
      downloadISOXMLOrder(fielFormdData, trailId).catch(() => {
        console.error("Error While Fetching API..!");
      });
    } else {
      downloadmonitorModalForm(data).catch(() => {
        console.error("Erroe While Fetching API..!");
      });
    }
    setModalOpen(false);
  };

  /**
   * Handles the cancel action for the monitor modal, resetting relevant state variables and closing the modal.
   */
  const handleCancel = (): void => {
    setModalOpen(false);
    setTrialId("");
    setfieldIds("");
    setExportAgOperationIds([]);
    setErrorMessage("");
  };

  /**
   * Closes the ag-operation loader modal and clears the export state it collected.
   */
  const hideAgLoader = (): void => {
    setAgLoaderOpen(false);
    setErrorMessage("");
    setExportAgOperationIds([]);
    setTrialId("");
    setfieldIds("");
  };

  /**
   * opens the modal for downloading work orders, setting relevant state variables based on the provided trial information.
   * @param trialId The ID of the trial for which the modal is being opened.
   * @param fieldIds The field IDs associated with the trial.
   * @param trialInfo The information about the trial, including associated agricultural operations.
   */
  const openModal = (
    trailId: string,
    fieldIds: string,
    trialInfo: any
  ): void => {
    void trackTrialsEvents({
      event: TrialAmplitudeEvent.TRIAL_DESIGN,
      originAction: "Download Popup",
    });
    setfieldIds(fieldIds);
    setTrialId(trailId);
    setExportAgOperationIds(
      Object.values(trialInfo?.ag_operations || {})
        .flat()
        .filter((value): value is string => typeof value === "string")
    );
    if (enableNewWorkorderExport) {
      // Open the loader modal; the effect below refetches the latest trial
      // data and then hands off to the export flow.
      setAgLoaderOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  /**
   * When the loader modal opens, refetch the latest data for the selected trial.
   * Once the fetch settles, either surface an error inside the loader or hide it
   * and hand off to the export flow.
   */
  useEffect(() => {
    if (!agLoaderOpen) return;
    // Start each open with a clean slate so a previous run's error is not
    // shown while the refetch is in flight.
    setErrorMessage("");
    if (!trailId) {
      setErrorMessage(formatTranslation(t("trials.missingAgOpIdError")));
      return;
    }
    let cancelled = false;
    void (async () => {
      // The row data the modal was opened from can be stale, so the ids/status
      // must be read from the freshly fetched payload.
      let latestTrial: any = null;
      try {
        latestTrial = await fetchTrialById(trailId);
      } catch (error) {
        console.error("Error fetching trial:", error);
      }
      if (cancelled) return;

      const fetchedAgOperationIds = Object.values(
        latestTrial?.ag_operations ?? {}
      )
        .flat()
        .filter((value): value is string => typeof value === "string");
      setExportAgOperationIds(fetchedAgOperationIds);

      const protocolType = trialData.find(
        (t) => t.id === trailId
      )?.protocol_type;

      if ((latestTrial?.ag_operation_status ?? "") !== "") {
        setErrorMessage(formatTranslation(t("trials.agOpCreationfailedError")));
      } else if (
        fetchedAgOperationIds.length === 0 ||
        fetchedAgOperationIds.length !== protocolType?.length
      ) {
        setErrorMessage(formatTranslation(t("trials.missingAgOpIdError")));
      } else {
        // Data is available — clear any previous error and hand off to the
        // export flow.
        setErrorMessage("");
        setAgLoaderOpen(false);
        setModalOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agLoaderOpen]);

  /**
   * Fetches trial data from the server based on the current organization, farm, pagination, search text, and completion filter.
   * Handles loading state and errors during the fetch operation. The fetched data is stored in the `trialData` state variable.
   * @returns trials
   */
  const fetchData = async (
    pagination: any,
    resetFilter: boolean = false,
    searchValue: string = searchText
  ): Promise<void> => {
    setLoading(true);
    try {
      const trialsData = await fetchTrials(
        orgId ?? "",
        farmId ?? "",
        pagination.current,
        pagination.pageSize,
        searchValue,
        resetFilter ? "all" : completionFilter
      );
      setTrialData(trialsData.trials || []);
      setPagination({ ...pagination, total: trialsData.count || 0 });
      setLoading(false);
      return trialsData.trials || [];
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  /**
   * handle table pagination change
   * @param pagination
   */
  const handlePagination = async (pagination: any): Promise<void> => {
    setPagination(pagination);
    await fetchData(pagination);
  };

  /**
   * handle search text change, updating the search text state and resetting the pagination to the first page.
   * @param value The new search text value.
   */
  const handleSearchTextChange = (value: string): void => {
    setSearchText(value);
    setPagination((currentPagination) => ({
      ...currentPagination,
      current: 1,
    }));
  };

  /**
   * Deletes a trial by its ID and refreshes the trial data.
   * @param id The ID of the trial to delete.
   * @returns A promise that resolves when the deletion is complete.
   */
  const handleDeleteTrialData = async (id: string): Promise<any> => {
    if (!id) {
      console.error("Cannot delete trial: trial ID is undefined");
      return;
    }
    void trackTrialsEvents({
      event: TrialAmplitudeEvent.DELETE_TRIAL,
      originAction: "Delete Trial",
    });
    await deleteTrial(id);
    await fetchData(pagination);
  };

  /**
   * opens the protocol drawer for a specific protocol ID, setting the relevant state variables to display the drawer with the appropriate content.
   * @param protocolId
   */
  const openDrawer = (protocolId: string): void => {
    if (protocolId) {
      setProtocolId(protocolId);
      setOpen(true);
    }
  };

  /**
   * Closes the protocol drawer by resetting the relevant state variables, effectively hiding the drawer from view.
   */
  const onClose = (): void => {
    setOpen(false);
  };

  /**
   * toggles the completion status of a trial by sending a PATCH request to the server with the updated completion value.
   * Upon successful update, it shows a success toast message and updates the local trial data state to reflect the change. If an error occurs, it shows an error toast message.
   * @param checked The new completion status to set for the trial.
   * @param trialId The ID of the trial for which the completion status is being toggled.
   * @returns A promise that resolves when the completion status has been toggled and the local state has been updated.
   */
  const toggleCompletion = async (
    checked: boolean,
    trialId: string
  ): Promise<any> => {
    if (!trialId) {
      console.error("Cannot toggle completion: trial ID is undefined");
      return;
    }

    try {
      const response = await markTrialComplete(trialId, checked);
      if (response) {
        toast.success(formatTranslation(t("trials.completionSuccess")));
        await fetchData(pagination);
        void trackTrialsEvents({
          event: TrialAmplitudeEvent.TRIAL_COMPLETION_TOGGLE,
          originAction: `trial-completion-set-${checked ? "true" : "false"}`,
        });
      } else {
        toast.error(formatTranslation(t("trials.completionError")));
      }
    } catch (error) {
      toast.error(formatTranslation(t("trials.completionError")));
    }
  };

  /**
   * Handles the toggle of a trial's completion status, showing modals if necessary based on the trial's current state.
   * @param checked The new completion status to set for the trial.
   * @param trialId The ID of the trial for which the completion status is being toggled.
   */
  const handleToggleCompletion = (checked: boolean, trialId: string): void => {
    setCompletionStatus(checked);
    if (checked) {
      // Switching from not-complete to completed: check if trial data is complete
      const trial = trialData.find((t) => t.id === trialId);
      const isOnSummaryStep =
        (trial?.completed_step != null && Number(trial.completed_step) >= 3) ||
        trial?.is_exportable === true;
      if (isOnSummaryStep) {
        void toggleCompletion(checked, trialId);
      } else {
        // Trial data incomplete: show incomplete modal
        setCompletionTrialId(trialId);
        setCompletionModalOpen(true);
      }
    } else {
      // Switching from completed to not-complete: show confirmation modal
      setCompletionTrialId(trialId);
      setCompletionModalOpen(true);
    }
  };

  /**
   * Handles the cancellation of the incomplete data modal, closing the modal and resetting the relevant state variable for the incomplete trial ID.
   */
  const handleCompletionModalCancel = (): void => {
    setCompletionModalOpen(false);
    setCompletionTrialId(null);
    setCompletionStatus(null);
  };

  /**
   * Handles the confirmation of the completion status change, toggling the completion status of the trial if there is a pending change, and then closing the confirmation modal and resetting the pending change state variable.
   */
  const handleCompletionModalProceed = (): void => {
    if (!completionStatus) {
      void toggleCompletion(false, completionTrialId as string);
    } else {
      router.goToTrialsEditPage(undefined, completionTrialId as string);
    }
    handleCompletionModalCancel();
  };

  /**
   * Defines the columns for the trial list table, including actions for deleting, opening modals, toggling completion, and sharing reports.
   */
  const trialColumns = TrialListColumns(
    handleDeleteTrialData,
    openModal,
    openDrawer,
    handleToggleCompletion,
    handleShareReport
  );

  /**
   * Navigates to the trial design page when the "Create Trial" button is clicked, and logs the event using Amplitude for analytics purposes.
   */
  const handleNavigate = (): void => {
    void trackTrialsEvents({
      event: TrialAmplitudeEvent.CREATE_TRIAL,
      originAction: "Create Trial",
    });
    router.goToTrialsCreatePage();
  };

  /**
   * Fetches trial data when the component mounts or when the organization or farm context changes.
   * Errors during fetching are caught and logged to the console.
   */
  useEffect(() => {
    const fetchDataAndHandleError = async (): Promise<void> => {
      try {
        await fetchData(pagination);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchDataAndHandleError().catch((error) => {
      console.error("Error handling fetchDataAndHandleError:", error);
      return error;
    });
  }, [orgId, farmId]);

  /**
   * Handles the search text change with a debounce effect. When the search text changes, it resets the pagination to the first page and fetches the trial data after a specified delay, allowing for efficient searching without excessive API calls.
   * The debounce effect ensures that the fetch operation is only triggered after the user has stopped typing for a certain period, improving performance and user experience.
   * @param searchText The current search text input by the user.
   */
  useEffect(() => {
    if (!hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      return;
    }

    const updatedPagination = {
      ...pagination,
      current: 1,
    };

    const timer = setTimeout(() => {
      void fetchData(updatedPagination, false, searchText);
    }, SEARCH_DEBOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  /**
   * Applies the selected completion filter and closes the dropdown.
   */
  const applyFilter = (reset?: boolean): void => {
    if (reset) {
      setCompletionFilter("all");
    }
    setDropdownVisible(false);
    void fetchData(pagination, reset);
  };

  return (
    <div className="create-trail-main">
      {isLoading && <Loader mask message="" />}
      {fieldIds && trailId && !enableNewWorkorderExport && (
        <TrailModal
          open={modalOpen}
          handleOk={handleOk}
          handleCancel={handleCancel}
          fieldId={fieldIds}
          trialId={trailId}
        />
      )}
      {fieldIds &&
        trailId &&
        enableNewWorkorderExport &&
        modalOpen &&
        exportAgOperationIds.length > 0 &&
        errorMessage === "" && (
          <PrescriptionExporterMfeLauncher
            isOpen={modalOpen}
            onClose={handleCancel}
            agOperationIds={exportAgOperationIds}
            userLanguage={i18n.language}
          />
        )}
      {enableNewWorkorderExport && agLoaderOpen && (
        <AgOperationDataLoader
          open={agLoaderOpen}
          onClose={hideAgLoader}
          errorMessage={errorMessage}
        />
      )}
      {protocolId && (
        <ProtocolDrawer
          protocolId={protocolId}
          open={open}
          onClose={onClose}
          extraHeaders={extraHeaders}
        />
      )}
      {selectedTrialInfo && (
        <TrialReportDrawer
          open={drawerVisible}
          onClose={hideDrawer}
          trialId={selectedTrialInfo.id}
          fieldIds={selectedTrialInfo.field_ids}
          reportData={reportData}
          onGenerateReport={handleGenerateReport}
          propertyId={selectedTrialInfo.property_id}
          noOfFields={selectedTrialInfo.field_ids?.length}
          noOfTrails={selectedTrialInfo.field_ids?.length}
          drawerView={drawerView}
          onEdit={() => {
            setDrawerView("tree");
          }}
          onTreeBack={() => {
            setDrawerView("share");
          }}
          treeCheckedKeys={treeCheckedKeys}
          setTreeCheckedKeys={setTreeCheckedKeys}
        />
      )}
      <TrialsPageHeader isLoading={isLoading} handleNavigate={handleNavigate} />
      <div className="body-content">
        <SearchAndFilter
          searchText={searchText}
          setSearchText={handleSearchTextChange}
          completionFilter={completionFilter}
          setCompletionFilter={setCompletionFilter}
          applyFilter={applyFilter}
          dropdownVisible={dropdownVisible}
          setDropdownVisible={setDropdownVisible}
        />
        <div className="table-sec">
          <Table
            columns={trialColumns}
            dataSource={trialData}
            pagination={
              pagination.total > pagination.pageSize
                ? { ...pagination, position: ["bottomCenter"] }
                : false
            }
            onChange={(pagination: any): void => {
              void handlePagination(pagination);
            }}
            locale={{
              emptyText: (
                <TableEmptyState isLoading={isLoading} trialData={trialData} />
              ),
            }}
            rowKey="id"
          />
          <ToastContainer />
          <CompletionConfirmationModal
            completionModalOpen={completionModalOpen}
            handleCompletionModalCancel={handleCompletionModalCancel}
            handleCompletionModalProceed={handleCompletionModalProceed}
            completionStatus={completionStatus}
          />
        </div>
      </div>
    </div>
  );
};

export default TrialsList;
